import React, { Dispatch, SetStateAction, useState } from 'react'
import {
    AnalyzeIcon,
    colors,
    CommentIcon,
    DiversityIcon,
    FeatherIcon,
    GoalIcon,
    HomeIcon,
    InclusionIcon,
    LumiShadedIcon,
    PainPointIcon,
    SaveIcon,
    SurveyIcon,
    ToolsIcon,
    TrainingsIcon,
    WriteIcon,
} from '@diversioteam/diversio-ds'
import { urlMap } from 'dashboardRoutes'

import { useGetActiveCompanyInformation } from 'hooks/useGetActiveCompany'
import { useVersion } from 'hooks/useVersion'

import { List } from './List'
import { LumiNavContent } from './Lumi'
import { PulseSurveys } from './PulseSurveys'
import { ExperienceDialog } from './PulseSurveys/ExperienceDialog'
import { PeopleDialog } from './PulseSurveys/PeopleDialog'
import { OptionProps } from './navbar.types'
import * as S from './navbar.styles'

const NAV_ICON_BASE_PROPS = {
    type: 'bold',
    color: colors.primary30,
} as const

export const Navbar = () => {
    const [isCollapsed, setIsCollapsed] = useState(true)

    const [isExperienceDialogOpen, setIsExperienceDialogOpen] = useState(false)
    const [isPeopleDialogOpen, setIsPeopleDialogOpen] = useState(false)

    let hoverTimeout: NodeJS.Timeout | null = null

    const {
        companyInformation: { logo, logoCollapsed, name, permissions },
    } = useGetActiveCompanyInformation()

    const { version, AVAILABLE_VERSIONS } = useVersion()

    const isV2Enabled = version === AVAILABLE_VERSIONS.v2

    const handleMouseEnter = () => {
        if (hoverTimeout) clearTimeout(hoverTimeout)

        hoverTimeout = setTimeout(() => {
            setIsCollapsed(false)
        }, 1000)
    }

    const handleMouseLeave = () => {
        if (hoverTimeout) clearTimeout(hoverTimeout)

        hoverTimeout = setTimeout(() => {
            setIsCollapsed(true)
        }, 300)
    }

    const handleToggleDialog = (setter: Dispatch<SetStateAction<boolean>>) => {
        setter((prev) => !prev)
    }

    const handleCollapse = () => {
        setIsCollapsed(true)
    }

    const rawNavItems: OptionProps[][] = [
        [
            {
                path: `/${urlMap.home}`,
                icon: <HomeIcon {...NAV_ICON_BASE_PROPS} />,
                label: 'NAVBAR.HOME',
                version: 'v1',
                hasPermission: permissions.home,
            },
            {
                path: `/${urlMap.home2}`,
                icon: <HomeIcon {...NAV_ICON_BASE_PROPS} />,
                label: 'NAVBAR.HOME2',
                version: 'v2',
                hasPermission: permissions.home,
            },
            {
                path: `/${urlMap.analyze}`,
                icon: <AnalyzeIcon {...NAV_ICON_BASE_PROPS} />,
                label: 'NAVBAR.ANALYZE',
                version: 'v1',
                hasPermission: permissions.analyze,
            },
            {
                path: `/${urlMap.analyze_diversity}`,
                icon: <DiversityIcon {...NAV_ICON_BASE_PROPS} />,
                label: 'NAVBAR.DIVERSITY',
                version: 'v2',
                hasPermission: permissions.analyze,
            },
            {
                path: `/${urlMap.analyze_inclusion}`,
                icon: <InclusionIcon {...NAV_ICON_BASE_PROPS} />,
                label: 'NAVBAR.INCLUSION',
                version: 'v2',
                hasPermission: permissions.analyze,
            },
            {
                path: `/${urlMap.bespoke}`,
                icon: <FeatherIcon {...NAV_ICON_BASE_PROPS} />,
                label: 'NAVBAR.BESPOKE',
                version: 'v1',
                hasPermission: permissions.bespoke,
            },
            {
                path: `/${urlMap.voyager}`,
                icon: <CommentIcon {...NAV_ICON_BASE_PROPS} />,
                label: 'NAVBAR.COMMUNICATION',
                version: 'v1',
                hasPermission: permissions.voyager,
            },
            {
                path: `/${urlMap.survey}`,
                icon: <WriteIcon {...NAV_ICON_BASE_PROPS} />,
                label: 'NAVBAR.SURVEY',
                version: 'v1',
                hasPermission: permissions.survey,
            },
            {
                path: `/${urlMap.iln_dashboard_home}`,
                icon: <HomeIcon {...NAV_ICON_BASE_PROPS} />,
                label: 'NAVBAR.HOME',
                version: 'v1',
                hasPermission: permissions.ilnDashboardHome,
            },
            {
                path: `/${urlMap.iln_dashboard_impact}`,
                icon: <PainPointIcon {...NAV_ICON_BASE_PROPS} />,
                label: 'NAVBAR.IMPACT',
                version: 'v1',
                hasPermission: permissions.ilnDashboardHome,
            },
        ],
        [
            {
                icon: <GoalIcon {...NAV_ICON_BASE_PROPS} />,
                label: 'NAVBAR.GOALS',
                path: `/${urlMap.solution_goals}`,
                options: [
                    { path: `/${urlMap.solution_goals}`, label: 'NAVBAR.GET_STARTED' },
                    { path: `/${urlMap.solution_programs}`, label: 'NAVBAR.PROGRAMS' },
                    { path: `/${urlMap.solution_recommended}`, label: 'NAVBAR.RECOMMENDED' },
                ],
                version: 'v2',
                hasPermission: permissions.solutionGoals,
            },
        ],
        [
            {
                path: `/${urlMap.solution_catalog}`,
                label: 'NAVBAR.RESOURCES',
                icon: <ToolsIcon {...NAV_ICON_BASE_PROPS} />,
                version: 'v2',
                hasPermission: permissions.solutionCatalog,
            },
            {
                path: `/${urlMap.solution_trainings}`,
                label: 'NAVBAR.TRAININGS',
                icon: <TrainingsIcon {...NAV_ICON_BASE_PROPS} />,
                version: 'v2',
                hasPermission: permissions.solutionTrainings,
            },
            {
                path: `/${urlMap.solution_my_solutions}`,
                icon: <SaveIcon {...NAV_ICON_BASE_PROPS} />,
                label: 'NAVBAR.MY_SOLUTIONS',
                version: 'v2',
                hasPermission: permissions.solutionMySolutions,
            },
            {
                path: `/${urlMap.pulseSurvey}`,
                label: 'NAVBAR.SURVEY',
                icon: <SurveyIcon {...NAV_ICON_BASE_PROPS} />,
                version: 'v2',
                hasPermission: permissions.pulseSurvey,
            },
            {
                path: `/${urlMap.lumi}`,
                icon: (
                    <div className="lumi-navbar">
                        <LumiShadedIcon height={20} width={20} />
                    </div>
                ),
                label: 'NAVBAR.INCLUSIVE_JOB_ADS',
                customNavContent: <LumiNavContent />,
                version: 'v2',
                hasPermission: permissions.lumiJobAds,
            },
        ],
    ]

    const navItems: OptionProps[][] = rawNavItems
        .map((item) => item.filter((option) => option.hasPermission && option.version === version))
        .filter((item) => item.length > 0)

    return (
        <S.NavbarWrapper onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} isCollapsed={isCollapsed}>
            <S.LogoContainer isCollapsed={isCollapsed}>
                <img src={logoCollapsed} alt={name} />

                <img src={logo} alt={name} />
            </S.LogoContainer>

            {navItems.map((options, key) => {
                return <List key={key} options={options} isCollapsed={isCollapsed} />
            })}

            {permissions.pulseSurvey && isV2Enabled && (
                <>
                    <PulseSurveys
                        isCollapsed={isCollapsed}
                        onCollapse={handleCollapse}
                        onOpenExperienceDialog={() => handleToggleDialog(setIsExperienceDialogOpen)}
                        onOpenPeopleDialog={() => handleToggleDialog(setIsPeopleDialogOpen)}
                    />

                    <ExperienceDialog
                        open={isExperienceDialogOpen}
                        onClose={() => handleToggleDialog(setIsExperienceDialogOpen)}
                    />

                    <PeopleDialog open={isPeopleDialogOpen} onClose={() => handleToggleDialog(setIsPeopleDialogOpen)} />
                </>
            )}
        </S.NavbarWrapper>
    )
}
